import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import Footer from "./components/layouts/Footer";

import LoginPage from "./pages/LoginPage";

import ProtectedRoute from "./components/ProtectedRoute";

import DashboardLayout from "./components/layouts/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Sailings from "./pages/Sailings";
import Bookings from "./pages/Bookings";
import Customers from "./pages/Customers";
import Settings from "./pages/Settings";

import SailingDetail from "./pages/SailingDetail";
import BookingDetail from "./pages/BookingDetail";
import CustomerDetail from "./pages/CustomerDetail";

import BookingRequestPage from "./pages/BookingRequestPage";
import PublicSchedule from "./pages/PublicSchedule";
import TermsPage from "./pages/TermsPage";
import Apply from "./pages/Apply"
import LivestockTerms from "./pages/LivestockTerms";
import NewBooking from "./pages/NewBooking";
import NewSailing from "./pages/NewSailing";
import NewCustomer from "./pages/NewCustomer";

import EditSailing from "./pages/EditSailing";
import EditBooking from "./pages/EditBooking";
import EditCustomer from "./pages/EditCustomer";

import AddVessel from "./pages/AddVessel";
import EditVessel from "./pages/EditVessel";
import RateSettings from "./pages/RateSettings";
import Livestock from "./pages/Livestock";
import Applications from "./pages/Applications";
import ApplicationDetail from "./pages/ApplicationDetail";
import LoadPlanPrint from "./pages/LoadPlanPrint";
import ManifestPrint from "./pages/ManifestPrint";
import BookingPrint from "./pages/BookingPrint";
import SailingSchedulePrint from "./pages/SailingSchedulePrint";

import CustomerPortalLogin from "./pages/CustomerPortalLogin";
import CustomerPortalForgotPassword from "./pages/CustomerPortalForgotPassword";
import CustomerPortalSetup from "./pages/CustomerPortalSetup";
import CustomerPortalLayout from "./pages/CustomerPortalLayout";
import CustomerPortalBookings from "./pages/CustomerPortalBookings";
import CustomerPortalNewRequest from "./pages/CustomerPortalNewRequest";
import CustomerPortalSailings from "./pages/CustomerPortalSailings";
import CustomerPortalProfile from "./pages/CustomerPortalProfile";

function AppRoutes() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");
  const isPortal = location.pathname.startsWith("/portal");
  // Root and /login now go straight to the (self-contained, full-screen)
  // login page — no separate marketing site lives in this app anymore, so
  // there's no TopBanner/Footer chrome to wrap around it either.
  const isLogin = location.pathname === "/" || location.pathname === "/login";
  const isBookingRequest = location.pathname === "/request-booking";
  const isSchedule = location.pathname === "/schedule";
  const isTerms = location.pathname === "/terms";
  const isApply = location.pathname === "/apply";
  const isLivestockTerms = location.pathname === "/livestock-terms";
  const hideChrome = isDashboard || isPortal || isLogin || isBookingRequest || isSchedule || isTerms || isApply || isLivestockTerms;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
      <main
        className={`flex-grow ${
          hideChrome ? "" : "container mx-auto px-4 py-8"
        }`}
      >
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/request-booking" element={<BookingRequestPage />} />

          {/* Public shipping schedule — no login; ?embed=1 for use inside the website */}
          <Route path="/schedule" element={<PublicSchedule />} />

          {/* Terms of Trade for Carriage of Freight */}
          <Route path="/terms" element={<TermsPage />} />

          {/* Livestock account application — public, no login */}
          <Route path="/apply" element={<Apply />} />
          <Route path="/livestock-terms" element={<LivestockTerms />} />

          {/* Customer Portal — separate auth from staff dashboard */}
          <Route path="/portal/login" element={<CustomerPortalLogin />} />
          <Route path="/portal/forgot-password" element={<CustomerPortalForgotPassword />} />
          <Route path="/portal/setup" element={<CustomerPortalSetup />} />

          <Route path="/portal" element={<CustomerPortalLayout />}>
            <Route index element={<CustomerPortalBookings />} />
            <Route path="new" element={<CustomerPortalNewRequest />} />
            <Route path="sailings" element={<CustomerPortalSailings />} />
            <Route path="profile" element={<CustomerPortalProfile />} />
            {/* Legacy path kept working in case it's linked anywhere else */}
            <Route path="request" element={<CustomerPortalNewRequest />} />
          </Route>

          {/* Print pages — deliberately OUTSIDE CustomerPortalLayout/DashboardLayout.
              Both layouts wrap their content in a fixed-height, overflow-y-auto
              scrolling container (for the fixed sidebar) — browsers can only
              print what's currently visible inside a scroll container, not
              content you'd have to scroll to see, so anything nested inside
              those layouts gets silently cut off in print/PDF output beyond
              one screen's worth of content. Print pages need to render as
              their own true standalone page, sidebar-free, to print correctly. */}
          <Route path="/portal/bookings/:id/print" element={<BookingPrint />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />

            <Route path="sailings" element={<Sailings />} />
            <Route path="sailings/new" element={<NewSailing />} />
            <Route path="sailings/:id" element={<SailingDetail />} />
            <Route path="sailings/:id/edit" element={<EditSailing />} />
            <Route path="sailings/edit/:id" element={<EditSailing />} />

            <Route path="bookings" element={<Bookings />} />
            <Route path="bookings/new" element={<NewBooking />} />
            <Route path="bookings/:ref" element={<BookingDetail />} />
            <Route path="bookings/:id/edit" element={<EditBooking />} />

            <Route path="customers" element={<Customers />} />
            <Route path="customers/new" element={<NewCustomer />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="customers/:id/edit" element={<EditCustomer />} />

            <Route path="settings" element={<Settings />} />
            <Route path="settings/rates" element={<RateSettings />} />
            <Route path="settings/vessels/new" element={<AddVessel />} />
            <Route path="settings/vessels/:id/edit" element={<EditVessel />} />

            <Route path="livestock" element={<Livestock />} />
            <Route path="applications" element={<Applications />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
          </Route>

          {/* Same fix applied here — was previously nested inside DashboardLayout
              and likely suffered the identical print-clipping issue. */}
          <Route
            path="/dashboard/sailings/:id/loadplan"
            element={
              <ProtectedRoute>
                <LoadPlanPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/sailings/:id/manifest/print"
            element={
              <ProtectedRoute>
                <ManifestPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/sailings/schedule/print"
            element={
              <ProtectedRoute>
                <SailingSchedulePrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/bookings/:id/print"
            element={
              <ProtectedRoute>
                <BookingPrint />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>

      {!hideChrome && <Footer />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;